<template>
  <div class="training-list">
    <div class="filters">
      <input
        v-model="filters.search"
        type="text"
        class="input-search"
        :placeholder="$t('bo.common.search')"
        @input="debouncedLoad"
      />
      <select v-model="filters.is_active" class="select-filter" @change="loadTrainings()">
        <option value="">{{ $t('bo.trainings.allStatuses') }}</option>
        <option value="1">{{ $t('bo.trainings.statusActive') }}</option>
        <option value="0">{{ $t('bo.trainings.statusInactive') }}</option>
      </select>
      <select v-model="filters.level" class="select-filter" @change="loadTrainings()">
        <option value="">{{ $t('bo.trainings.allLevels') }}</option>
        <option value="beginner">{{ $t('bo.trainings.levelBeginner') }}</option>
        <option value="intermediate">{{ $t('bo.trainings.levelIntermediate') }}</option>
        <option value="advanced">{{ $t('bo.trainings.levelAdvanced') }}</option>
      </select>
    </div>

    <div v-if="loading" class="state-msg">{{ $t('bo.common.loading') }}</div>
    <div v-else-if="error" class="state-msg error">{{ error }}</div>
    <div v-else-if="trainings.length === 0" class="state-msg empty">
      <div class="empty-icon">📭</div>
      <div>{{ $t('bo.trainings.noTrainings') }}</div>
    </div>

    <template v-else>
      <div class="list-meta">{{ $t('bo.trainings.totalCount', { count: pagination.total }) }}</div>

      <table class="table">
        <thead>
          <tr>
            <th>{{ $t('bo.trainings.colTitle') }}</th>
            <th>{{ $t('bo.trainings.colProvider') }}</th>
            <th>{{ $t('bo.trainings.colLevel') }}</th>
            <th>{{ $t('bo.trainings.colCredits') }}</th>
            <th>{{ $t('bo.trainings.colStatus') }}</th>
            <th>{{ $t('bo.trainings.colUpdated') }}</th>
            <th>{{ $t('bo.common.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in trainings" :key="row.id">
            <td class="col-title">
              <div class="row-title">{{ row.title }}</div>
              <div v-if="row.category" class="row-meta">{{ row.category }}</div>
            </td>
            <td>{{ row.provider || '—' }}</td>
            <td>{{ levelLabel(row.level) }}</td>
            <td>{{ formatCredits(row.credit_cost) }}</td>
            <td>
              <span :class="['badge-status', row.is_active ? 'st-active' : 'st-inactive']">
                {{ row.is_active ? $t('bo.trainings.statusActive') : $t('bo.trainings.statusInactive') }}
              </span>
            </td>
            <td class="col-date">{{ formatDate(row.updated_at) }}</td>
            <td class="actions">
              <button type="button" class="btn-action btn-edit" @click="$emit('edit', row)">
                {{ $t('bo.common.edit') }}
              </button>
              <button type="button" class="btn-action btn-archive" @click="$emit('archive', row.id)">
                {{ $t('bo.trainings.archive') }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="pagination.last_page > 1" class="pagination">
        <button
          type="button"
          class="page-btn"
          :disabled="pagination.current_page === 1"
          @click="loadPage(pagination.current_page - 1)"
        >
          {{ $t('bo.common.previous') }}
        </button>
        <span class="page-info">
          {{ $t('bo.translations.pageInfo', { current: pagination.current_page, last: pagination.last_page }) }}
        </span>
        <button
          type="button"
          class="page-btn"
          :disabled="pagination.current_page === pagination.last_page"
          @click="loadPage(pagination.current_page + 1)"
        >
          {{ $t('bo.common.next') }}
        </button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import trainingAdminService from '@/services/trainingAdminService'

const { t } = useI18n()

defineEmits(['edit', 'archive'])

const trainings = ref([])
const loading = ref(false)
const error = ref('')
const pagination = ref({ current_page: 1, last_page: 1, total: 0 })
const filters = reactive({ search: '', is_active: '', level: '' })

let debounceTimer = null
const debouncedLoad = () => {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => loadTrainings(), 300)
}

const extractList = (res) => {
  const body = res.data
  // Contrat TrainingController::index → { status, data: [...], meta }
  if (Array.isArray(body?.data)) {
    return { list: body.data, meta: body.meta || {} }
  }
  // Fallback pagination Laravel Resource wrap
  const nested = body?.data
  if (Array.isArray(nested?.data)) {
    return {
      list: nested.data,
      meta: {
        current_page: nested.current_page,
        last_page: nested.last_page,
        total: nested.total,
      },
    }
  }
  return { list: [], meta: {} }
}

const loadTrainings = async (page = 1) => {
  loading.value = true
  error.value = ''
  try {
    const params = { page, ...filters }
    Object.keys(params).forEach((k) => params[k] === '' && delete params[k])
    const res = await trainingAdminService.getAll(params)
    const { list, meta } = extractList(res)
    trainings.value = list
    pagination.value = {
      current_page: meta.current_page || 1,
      last_page: meta.last_page || 1,
      total: meta.total || list.length,
    }
  } catch {
    error.value = t('bo.trainings.failedLoad')
  } finally {
    loading.value = false
  }
}

const loadPage = (page) => loadTrainings(page)

const levelLabel = (level) =>
  ({
    beginner: t('bo.trainings.levelBeginner'),
    intermediate: t('bo.trainings.levelIntermediate'),
    advanced: t('bo.trainings.levelAdvanced'),
  })[level] || '—'

const formatCredits = (value) => (value === null || value === undefined ? '—' : Number(value))
const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '—'

defineExpose({ loadTrainings })
onMounted(() => loadTrainings())
</script>

<style scoped>
.filters {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
}

.input-search {
  flex: 1;
  min-width: 180px;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.select-filter {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: white;
}

.state-msg {
  padding: 3rem;
  text-align: center;
  color: #6b7280;
}

.state-msg.error {
  color: #dc2626;
}

.empty-icon {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.training-list {
  background: white;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.list-meta {
  padding: 0.75rem 1.25rem;
  font-size: 0.875rem;
  color: #6b7280;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.table th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.table td {
  padding: 0.8rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
}

.table tr:last-child td {
  border-bottom: none;
}

.col-title {
  max-width: 280px;
}

.col-date {
  white-space: nowrap;
  color: #6b7280;
  font-size: 0.8rem;
}

.row-title {
  font-weight: 600;
  color: #1f2937;
}

.row-meta {
  font-size: 0.78rem;
  color: #9ca3af;
  margin-top: 0.15rem;
}

.badge-status {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.st-active {
  background: #d1fae5;
  color: #065f46;
}

.st-inactive {
  background: #f3f4f6;
  color: #6b7280;
}

.actions {
  white-space: nowrap;
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.btn-action {
  padding: 0.3rem 0.65rem;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 600;
}

.btn-edit {
  background: #e0e7ff;
  color: #3730a3;
}

.btn-edit:hover {
  background: #c7d2fe;
}

.btn-archive {
  background: #f3f4f6;
  color: #6b7280;
}

.btn-archive:hover {
  background: #e5e7eb;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
}

.page-btn {
  padding: 0.4rem 0.9rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
}

.page-btn:hover:not(:disabled) {
  background: #f9fafb;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 0.875rem;
  color: #6b7280;
}
</style>
